import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { toastSuccess } from '@components/Toast';

import ConfirmationModal from '@features/crm/components/ConfirmationModal';
import {
	allFilterPages,
	generateSketchListParams,
	sketchListFields
} from '@features/filter/static/filterUtil';
import FilterComponent from '@features/filter/v2/FilterComponent';
import AddSketchModal from '@features/assets/components/sketch/AddSketchModal';
import SketchItem from '@features/assets/components/sketch/SketchItem';

import {
	addItemInGlobalFilterData,
	clearFilterResponse,
	handleFilter,
	removeItemFromFilterResponse,
	updateGlobalFilterData
} from '@store/actions/FilterAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { deleteSketch, downloadSketchImages, getSketchResponse } from '@services/assetsService';
import { fetchDependentFilterList } from '@services/commonService';

import { downloadBlobFIle, isPageReachBottom, printApiError } from '@utils/Util';

const SketchList = ({ isShowFilter, setTotalElements, showAddModal, setShowAddModal }) => {
	const [saveType, setSaveType] = useState('add');
	const [sketchList, setSketchList] = useState(null);
	const [selectedSketchId, setSelectedSketchId] = useState(null);
	const [selectedIdToDelete, setSelectedIdToDelete] = useState(null);
	const [sketchResponse, setSketchResponse] = useState(null);
	const [page, setPage] = useState(0);
	const filterResponse = useSelector((store) => store.filterResponseStore);
	let filterfields = useSelector((store) => store.filterFieldStore);
	const [openConfirmation, setOpenConfirmation] = useState(false);
	const sortValue = filterfields['sort'] && filterfields['sort'][0]?.key;
	const { ...paramsData } = generateSketchListParams(filterfields);
	const dispatch = useDispatch();
	const scrollRef = useRef({});

	const setCurrentState = (data) => {
		scrollRef.current = data;
	};

	useEffect(() => {
		if (filterResponse?.isSketch) {
			setCurrentState(filterResponse);
			setSketchList(filterResponse);
			setTotalElements(filterResponse.totalElements);
		}
	}, [filterResponse]);

	useEffect(() => {
		window.scrollTo(0, 0);
		dispatch(openLoader());
		dispatch(fetchDependentFilterList({ type: 'SKETCH_LIST' }, paramsData));
		setPage(0);
		let queryParams = generateSketchListParams(filterfields, 0, sortValue);
		dispatch(handleFilter('getAllSkectch', queryParams, false)).finally(() => {
			dispatch(closeLoader());
		});
	}, [filterfields]);

	useEffect(() => {
		if (page !== 0) {
			dispatch(openLoader());
			let queryParams = generateSketchListParams(filterfields, page, sortValue);
			dispatch(handleFilter('getAllSkectch', queryParams, true)).finally(() => {
				dispatch(closeLoader());
			});
		}
	}, [page]);

	const onActionSucces = (responseData) => {
		if (saveType === 'edit') {
			dispatch(updateGlobalFilterData(responseData));
		} else {
			// onTotalElementsUpdate();
			dispatch(addItemInGlobalFilterData(responseData));
		}
		dispatch(fetchDependentFilterList({ type: 'SKETCH_LIST' }, paramsData));
	};

	useEffect(() => {
		document.addEventListener('scroll', handleScroll);
		return () => {
			dispatch(clearFilterResponse());
			document.removeEventListener('scroll', handleScroll);
		};
	}, []);

	const handleScroll = () => {
		if (isPageReachBottom()) {
			let { totalElements, currentPage, data } = scrollRef.current;
			if (totalElements !== data?.length) {
				setPage(() => currentPage + 1);
			}
		}
	};

	const onDownloadImages = async (sketchId) => {
		dispatch(openLoader());
		try {
			const data = await downloadSketchImages(sketchId);
			downloadBlobFIle(data);
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const onEdit = async (id) => {
		setShowAddModal(true);
		setSelectedSketchId(id);
		setSaveType('edit');
	};

	const onCloseModal = () => {
		setShowAddModal(false);
		setSelectedSketchId(null);
		setSketchResponse(null);
		setSaveType('');
	};

	const fetchSketchData = async () => {
		if (selectedSketchId) {
			try {
				dispatch(openLoader());
				const data = await getSketchResponse(selectedSketchId);
				setSketchResponse(data);
				dispatch(closeLoader());
			} catch {
				dispatch(closeLoader());
			}
		}
	};

	const onCloseConfirmationModal = () => {
		setOpenConfirmation(false);
	};

	const onDeleteSketch = async () => {
		dispatch(openLoader());
		try {
			await deleteSketch(selectedIdToDelete);
			dispatch(removeItemFromFilterResponse(selectedIdToDelete));
			dispatch(closeLoader());
			setOpenConfirmation(false);
			toastSuccess('Successfully deleted');
		} catch (error) {
			dispatch(closeLoader());
			printApiError(error);
		}
	};

	const onDeleteClick = (id) => {
		setSelectedIdToDelete(id);
		setOpenConfirmation(true);
	};

	useEffect(() => {
		fetchSketchData();
	}, [selectedSketchId]);

	const renderSketchList = () => {
		if (sketchList?.data?.length > 0) {
			return sketchList.data.map((sketch, index) => {
				return (
					<SketchItem
						key={`sketch_item_${index}`}
						sketch={sketch}
						onDownloadImages={onDownloadImages}
						onEdit={onEdit}
						onDeleteClick={onDeleteClick}
					/>
				);
			});
		}
	};

	return (
		<>
			<section className="filter-data-container filter-v3-data-container under-inner-tabs bg-white">
				<FilterComponent
					filterPageName={allFilterPages.SKETCH_LIST}
					fields={sketchListFields}
					showFilter={isShowFilter}
					isDependentFilter={true}
				/>
				<div className="data-table bg-white">
					<div className="design-list-container">
						<div className="design-list-row">{renderSketchList()}</div>
					</div>
				</div>
			</section>

			{showAddModal && (
				<AddSketchModal
					sketchResponse={sketchResponse}
					onCloseModal={onCloseModal}
					saveType={saveType}
					seketchId={selectedSketchId}
					onActionSucces={onActionSucces}
				/>
			)}

			{openConfirmation && (
				<ConfirmationModal
					title={'Delete sketch'}
					closeModal={onCloseConfirmationModal}
					description={' about deleting this sketch?'}
					callback={onDeleteSketch}
					footerType={'CLOSE'}
				/>
			)}
		</>
	);
};

export default SketchList;
